import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, cfmt, pfmt } from '../lib/api';
import { TID } from '../constants/testIds';
import { X, Plus } from 'lucide-react';

export default function Compare() {
    const [params, setParams] = useSearchParams();
    const initial = (params.get('slugs') || '').split(',').filter(Boolean);
    const [slugs, setSlugs] = useState(initial);

    const { data: all } = useQuery({
        queryKey: ['compare-all'],
        queryFn: async () => (await api.get('/neighbourhoods')).data,
    });

    const { data: cmp } = useQuery({
        queryKey: ['compare', slugs.join(',')],
        queryFn: async () => (await api.get('/neighbourhoods/compare', { params: { slugs: slugs.join(',') } })).data,
        enabled: slugs.length > 0,
    });

    useEffect(() => {
        if (slugs.length) setParams({ slugs: slugs.join(',') });
        else setParams({});
    }, [slugs, setParams]);

    function addSlug(slug) {
        if (slugs.length >= 3 || slugs.includes(slug)) return;
        setSlugs((s) => [...s, slug]);
    }
    function removeSlug(slug) {
        setSlugs((s) => s.filter((x) => x !== slug));
    }

    const items = cmp?.items || [];
    const openSlots = Math.max(0, 3 - items.length);

    const rows = [
        { l: 'City', v: (n) => `${n.city}, ${n.province}` },
        { l: 'Tagline', v: (n) => n.tagline, italic: true },
        { l: 'Median price', v: (n) => cfmt(n.median_price), mono: true },
        { l: 'Median rent (2BR)', v: (n) => cfmt(n.median_rent_2br), mono: true },
        { l: '1Y change', v: (n) => `${n.price_1yr_change_pct >= 0 ? '+' : ''}${pfmt(n.price_1yr_change_pct)}`, mono: true, tone: (n) => n.price_1yr_change_pct >= 0 ? 'up' : 'down' },
        { l: '5Y change', v: (n) => `+${pfmt(n.price_5yr_change_pct)}`, mono: true },
        { l: '5Y forecast', v: (n) => `+${pfmt(n.forecast_5yr_pct)}`, mono: true, tone: () => 'brand' },
        { l: 'Walkability', v: (n) => n.metrics.walkability, mono: true },
        { l: 'Schools', v: (n) => n.metrics.schools, mono: true },
        { l: 'Safety', v: (n) => n.metrics.safety, mono: true },
        { l: 'Family', v: (n) => n.metrics.family, mono: true },
        { l: 'Transit', v: (n) => n.metrics.transit, mono: true },
        { l: 'Green space', v: (n) => n.metrics.green_space, mono: true },
        { l: 'Nightlife', v: (n) => n.metrics.nightlife, mono: true },
        { l: 'Median age', v: (n) => n.demographics.median_age, mono: true },
        { l: 'Owner-occupied', v: (n) => `${n.demographics.owner_pct}%`, mono: true },
        { l: 'Household income', v: (n) => cfmt(n.demographics.avg_income), mono: true },
        { l: 'Commute (min)', v: (n) => `${n.commute_downtown_min} min`, mono: true },
    ];

    return (
        <div>
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-8">
                    <div className="overline">Compare</div>
                    <h1 className="font-editorial text-[44px] lg:text-[58px] tracking-editorial mt-4 leading-[1.04]">
                        Two or three, side by side.
                    </h1>
                    <p className="mt-4 text-[15px] text-neutral-600 max-w-2xl">
                        Every number, every score, every trend — presented so the choice is obvious.
                    </p>
                </div>
            </section>

            <section>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                    <div className="grid grid-cols-1" data-testid={TID.compare.table}>
                        <div className="grid" style={{ gridTemplateColumns: `240px repeat(${items.length + openSlots}, minmax(220px, 1fr))` }}>
                            <div className="p-4 border-b border-r border-neutral-200 overline">Compare</div>
                            {items.map((n) => (
                                <div key={n.slug} className="border-b border-r border-neutral-200 p-4 relative">
                                    <button data-testid={TID.compare.remove} onClick={() => removeSlug(n.slug)} className="absolute top-2 right-2 text-neutral-400 hover:text-black">
                                        <X className="w-4 h-4" />
                                    </button>
                                    <div className="font-editorial text-[22px] tracking-editorial">{n.name}</div>
                                    <div className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 mt-1">{n.city}</div>
                                </div>
                            ))}
                            {Array.from({ length: openSlots }).map((_, i) => (
                                <div key={i} className="border-b border-r border-neutral-200 p-4">
                                    <div className="overline mb-2">Add neighbourhood</div>
                                    <select
                                        data-testid={TID.compare.add}
                                        onChange={(e) => e.target.value && addSlug(e.target.value)}
                                        value=""
                                        className="w-full border border-neutral-300 px-2 py-2 text-[13px] bg-white"
                                       
                                    >
                                        <option value="">Select…</option>
                                        {(all?.items || []).filter((n) => !slugs.includes(n.slug)).map((n) => (
                                            <option key={n.slug} value={n.slug}>{n.name} · {n.city}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}

                            {items.length > 0 && rows.map((r, ri) => (
                                <FragmentRow key={r.l} row={r} items={items} openSlots={openSlots} zebra={ri % 2 === 0} />
                            ))}
                        </div>
                        {items.length === 0 && (
                            <div className="border border-neutral-200 p-16 text-center text-neutral-500">
                                Add a neighbourhood to begin comparing.
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

function FragmentRow({ row, items, openSlots, zebra }) {
    const toneColor = (t) => t === 'up' ? 'text-[#0B8A3E]' : t === 'down' ? 'text-[#C3341C]' : t === 'brand' ? 'text-[#2F6B47]' : '';
    return (
        <>
            <div className={`p-4 border-b border-r border-neutral-200 text-[12px] uppercase tracking-[0.14em] text-neutral-600 ${zebra ? 'bg-[#F7F7F5]' : ''}`}>{row.l}</div>
            {items.map((n) => {
                const val = row.v(n);
                const tone = row.tone ? row.tone(n) : null;
                return (
                    <div key={n.slug + row.l} className={`p-4 border-b border-r border-neutral-200 ${zebra ? 'bg-[#F7F7F5]' : ''}`}>
                        <span className={`${row.mono ? 'font-mono-data tabular text-[15px]' : row.italic ? 'font-editorial italic text-[15px]' : 'text-[14px]'} ${toneColor(tone)}`}>
                            {val}
                        </span>
                    </div>
                );
            })}
            {Array.from({ length: openSlots }).map((_, i) => (
                <div key={i + row.l} className={`p-4 border-b border-r border-neutral-200 ${zebra ? 'bg-[#F7F7F5]' : ''}`}>—</div>
            ))}
        </>
    );
}
